import React, { useState } from 'react';
import { MapPin, Mail, Send, Loader2, Building2 } from 'lucide-react';
import axios from 'axios';
import { ShimmerButton } from '../components/magicui/shimmer-button';
import { PageIntro } from '../components/PageIntro';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { company } from '../data/company';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: 'Poruka poslana',
        description: 'Hvala vam! Javit ćemo vam se u najkraćem mogućem roku.',
      });
      
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Greška',
        description: 'Došlo je do greške. Pokušajte ponovo.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="premium-page">
      <div className="premium-container">
        <PageIntro title="Razgovarajmo o svjetlu." testId="contact-title"><p data-testid="contact-introduction">Tu smo za pitanja o rasvjeti i vašem prostoru.</p></PageIntro>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="premium-contact-details space-y-8 min-w-0">
            <div>
              <h2 className="text-base md:text-lg font-medium mb-6" data-testid="contact-information-heading">Kontakt informacije</h2>
              <p className="font-semibold leading-relaxed mb-2" data-testid="contact-company-name">{company.name}</p>
              <p className="text-gray-600 mb-6" data-testid="contact-company-oib">OIB: {company.oib}</p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="premium-contact-icon">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold mb-1">Adresa</h3>
                    <p className="text-gray-600" data-testid="contact-address">{company.street}<br />{company.postalCode} {company.city}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="premium-contact-icon">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold mb-1">Email</h3>
                    <a href={`mailto:${company.email}`} className="text-gray-600 hover:text-gray-900 break-all" data-testid="contact-email-link">
                      {company.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="premium-contact-icon"><Building2 className="w-6 h-6" aria-hidden="true" /></div>
                  <div className="min-w-0">
                    <h3 className="font-semibold mb-1">IBAN</h3>
                    <p className="text-gray-600 break-all" data-testid="contact-iban">{company.iban}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-medium mb-4" data-testid="contact-hours-heading">Radno vrijeme</h3>
              <div className="space-y-2 text-gray-600" data-testid="contact-opening-hours">
                <p>Ponedjeljak - Petak: 08:00 - 17:00</p>
                <p>Subota: 09:00 - 14:00</p>
                <p>Nedjelja: Zatvoreno</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="premium-contact-form premium-form min-w-0">
            <h2 className="text-base md:text-lg font-medium mb-6" data-testid="contact-form-heading">Pošaljite nam poruku</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name">Vaše ime *</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Vaš email *</Label>
                <Input
                  id="email"
                  data-testid="contact-email-input"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Vaš telefon/mob</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="message">Poruka *</Label>
                <textarea
                  id="message"
                  data-testid="contact-message-input"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>

              <ShimmerButton type="submit" className="w-full" disabled={submitting} data-testid="contact-submit-button">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Slanje...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Pošalji upit
                  </>
                )}
              </ShimmerButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
